"use client";

import { motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useState } from "react";
import { useCursor } from "@/providers/cursor-provider";
import { cn } from "@/lib/utils";

export function CustomCursor() {
  const { label } = useCursor();
  const [enabled, setEnabled] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [visible, setVisible] = useState(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 260, damping: 28, mass: 0.55 });
  const ringY = useSpring(y, { stiffness: 260, damping: 28, mass: 0.55 });
  const dotX = useSpring(x, { stiffness: 700, damping: 36, mass: 0.3 });
  const dotY = useSpring(y, { stiffness: 700, damping: 36, mass: 0.3 });

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced) return;

    setEnabled(true);
    document.documentElement.classList.add("custom-cursor-active");

    const move = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
    };
    const down = () => setPressed(true);
    const up = () => setPressed(false);
    const leave = () => setVisible(false);
    const enter = () => setVisible(true);

    window.addEventListener("mousemove", move);
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.documentElement.addEventListener("mouseleave", leave);
    document.documentElement.addEventListener("mouseenter", enter);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.documentElement.removeEventListener("mouseleave", leave);
      document.documentElement.removeEventListener("mouseenter", enter);
      document.documentElement.classList.remove("custom-cursor-active");
    };
  }, [x, y]);

  if (!enabled) return null;

  const active = Boolean(label);

  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none fixed inset-0 z-[100] transition-opacity duration-200",
        visible ? "opacity-100" : "opacity-0",
      )}
    >
      {/* Thin trailing ring — outline only, no blur / no glass */}
      <motion.div
        className="absolute left-0 top-0"
        style={{ x: ringX, y: ringY, translateX: "-50%", translateY: "-50%" }}
      >
        <motion.div
          animate={{
            width: active ? 52 : pressed ? 18 : 34,
            height: active ? 52 : pressed ? 18 : 34,
            borderColor: active
              ? "color-mix(in srgb, var(--accent) 70%, transparent)"
              : "color-mix(in srgb, var(--accent) 45%, transparent)",
          }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="rounded-full border-[1.5px] bg-transparent"
        />
      </motion.div>

      {/* Crisp center point */}
      <motion.div
        className="absolute left-0 top-0"
        style={{ x: dotX, y: dotY, translateX: "-50%", translateY: "-50%" }}
      >
        <motion.div
          animate={{
            scale: pressed ? 0.78 : 1,
            opacity: 1,
          }}
          transition={{ type: "spring", stiffness: 480, damping: 28 }}
          className="h-2 w-2 rounded-full bg-[linear-gradient(135deg,var(--accent)_0%,var(--cyan)_100%)] shadow-[0_0_12px_color-mix(in_srgb,var(--accent)_50%,transparent)]"
        />
      </motion.div>

      {/* Label chip sits above the pointer so page text stays sharp */}
      <motion.div
        className="absolute left-0 top-0"
        style={{ x: dotX, y: dotY, translateX: "-50%", translateY: "calc(-50% - 2.1rem)" }}
      >
        <motion.div
          animate={{
            opacity: active ? 1 : 0,
            y: active ? 0 : 6,
            scale: active ? 1 : 0.92,
          }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="whitespace-nowrap rounded-md border border-[color-mix(in_srgb,var(--accent)_28%,transparent)] bg-[color-mix(in_srgb,var(--background-elevated)_96%,transparent)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground shadow-[0_8px_24px_rgba(11,40,90,0.14)]"
        >
          {label}
        </motion.div>
      </motion.div>
    </div>
  );
}
